import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Home, Sprout, Wheat, CloudRain, TrendingUp, Leaf, HandshakeIcon, Fish, MapPin, CalendarIcon, DollarSignIcon, CheckCircle2 } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
const detailedProjects = [{
  title: 'Agriculture Cluster Development Project (ACDP)',
  description: 'The Agriculture Cluster Development Project (ACDP) is a 6-year project implemented by MAAIF with support from the World Bank. The project aims to raise on-farm productivity, production, and marketable volumes of selected agricultural commodities in specified geographic clusters.',
  status: 'Active',
  funder: 'World Bank / Government of Uganda',
  period: '2015 – 2025',
  icon: Wheat,
  color: 'bg-amber-600',
  budget: '$150 Million',
  highlights: ['Supports 12 priority commodities including maize, beans, cassava, rice, and coffee.', 'Targets 58 districts grouped into 12 agro-ecological clusters.', 'Strengthens agricultural extension services and market linkages.', 'Provides matching grants for post-harvest handling and value addition facilities.']
}, {
  title: 'Regional Pastoral Livelihoods Resilience Project (RPLRP)',
  description: 'RPLRP is designed to enhance livelihood resilience of pastoral and agro-pastoral communities in cross-border drought-prone areas of Uganda, Kenya, and Ethiopia. It focuses on improving natural resource management and market access.',
  status: 'Active',
  funder: 'World Bank / IGAD',
  period: '2014 – 2024',
  icon: MapPin,
  color: 'bg-orange-600',
  budget: '$40 Million (Uganda Component)',
  highlights: ['Covers 12 districts in the Karamoja sub-region and neighboring areas.', 'Develops water infrastructure (valley tanks and dams) for livestock.', 'Improves animal health services and disease surveillance.', 'Builds climate resilience and early warning systems for pastoralists.']
}, {
  title: 'Agriculture Value Chain Development Program (AVCDP)',
  description: 'AVCDP focuses on strengthening priority agricultural value chains to improve productivity, value addition, and market access for smallholder farmers. The program aims to commercialize agriculture and increase household incomes.',
  status: 'Active',
  funder: 'African Development Bank',
  period: '2018 – 2025',
  icon: TrendingUp,
  color: 'bg-emerald-600',
  budget: '$76 Million',
  highlights: ['Focuses on rice, cassava, maize, and dairy value chains.', 'Supports agro-processing, packaging, and value addition infrastructure.', 'Targets youth and women entrepreneurship in agriculture.', 'Rehabilitates rural access roads to connect farmers to markets.']
}, {
  title: 'Uganda Climate Smart Agriculture Program',
  description: 'This program promotes climate-smart agricultural practices to increase productivity, enhance resilience to climate change, and reduce greenhouse gas emissions in the agricultural sector.',
  status: 'Active',
  funder: 'Green Climate Fund / FAO',
  period: '2020 – 2027',
  icon: CloudRain,
  color: 'bg-sky-600',
  budget: '$54 Million',
  highlights: ['Promotes drought-resistant and early-maturing crop varieties.', 'Implements sustainable land and water management practices.', 'Provides localized weather information services for farmers.', 'Supports afforestation and agroforestry integration.']
}, {
  title: 'Project for Restoration of Livelihoods in Northern Region (PRELNOR)',
  description: 'PRELNOR supports smallholder farmers in Northern Uganda to improve food security, increase incomes, and build resilient livelihoods after decades of conflict. It uses a community-driven development approach.',
  status: 'Active',
  funder: 'IFAD / Government of Uganda',
  period: '2015 – 2024',
  icon: HandshakeIcon,
  color: 'bg-violet-600',
  budget: '$70 Million',
  highlights: ['Covers 9 districts in the Acholi sub-region.', 'Develops rural infrastructure including community access roads and markets.', 'Empowers farmer groups and cooperatives.', 'Promotes climate-resilient farming techniques.']
}, {
  title: 'Multi-Sectoral Food Security & Nutrition Project (MSFSNP)',
  description: 'MSFSNP addresses food insecurity and malnutrition through an integrated multi-sectoral approach combining agriculture, health, and education. It specifically targets stunting among children under five and maternal nutrition.',
  status: 'Active',
  funder: 'World Bank / Global Agriculture & Food Security Program',
  period: '2017 – 2025',
  icon: Sprout,
  color: 'bg-green-600',
  budget: '$27 Million',
  highlights: ['Promotes production and consumption of micronutrient-rich foods.', 'Establishes school and community demonstration gardens.', 'Provides nutrition education through lead mothers and community groups.', 'Targets vulnerable households in 15 high-burden districts.']
}, {
  title: 'Aquaculture Development Project',
  description: 'This project aims to develop sustainable aquaculture and fisheries management to boost fish production, create employment, and improve nutrition outcomes across the country.',
  status: 'Active',
  funder: 'IsDB / Government of Uganda',
  period: '2019 – 2026',
  icon: Fish,
  color: 'bg-blue-600',
  budget: '$35 Million',
  highlights: ['Establishes aquaculture parks and cage fish farming on major lakes.', 'Supports private sector hatchery and quality fish feed production.', 'Improves fish quality assurance and safety standards.', 'Trains fish farmers in modern aquaculture practices.']
}, {
  title: 'Sustainable Land Management Project',
  description: 'Combating land degradation and promoting sustainable land management practices to protect agricultural productivity, restore degraded ecosystems, and conserve natural resources.',
  status: 'Active',
  funder: 'GEF / World Bank',
  period: '2018 – 2025',
  icon: Leaf,
  color: 'bg-teal-600',
  budget: '$20 Million',
  highlights: ['Implements soil and water conservation structures.', 'Promotes agroforestry and community reforestation.', 'Develops community watershed management plans.', 'Supports alternative livelihood activities to reduce pressure on land.']
}];
export function ProjectsPage() {
  return <>
      <Header data-id="element-1379" />
      <main className="flex-grow pt-[160px] md:pt-[148px] bg-gray-50 pb-20" data-id="element-1380">
        {/* Hero Section */}
        <div className="relative text-white py-32 md:py-48 overflow-hidden" data-id="element-1381">
          <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: 'url("https://cdn.magicpatterns.com/uploads/1RTqreGNtNUkoQPMUTyErX/fdhjkl_(7).jpg")'
        }} data-id="element-1382"></div>
          <div className="absolute inset-0 bg-uganda-green/80" data-id="element-1383"></div>
          <div className="relative max-w-7xl mx-auto px-4 md:px-8" data-id="element-1384">
            {/* Breadcrumb */}
            <div className="flex items-center text-sm text-green-100 mb-6" data-id="element-1385">
              <Link to="/" className="hover:text-white flex items-center" data-id="element-1386">
                <Home className="w-4 h-4 mr-1" data-id="element-1387" /> Home
              </Link>
              <ChevronRight className="w-4 h-4 mx-2" data-id="element-1388" />
              <span data-id="element-1389">Programs & Projects</span>
              <ChevronRight className="w-4 h-4 mx-2" data-id="element-1390" />
              <span className="text-white font-medium" data-id="element-1391">MAAIF Projects</span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-1392">
              MAAIF Projects
            </h1>
            <p className="text-lg text-green-50 max-w-3xl leading-relaxed" data-id="element-1393">
              Discover the strategic initiatives and projects implemented by the
              Ministry of Agriculture, Animal Industry and Fisheries to
              transform Uganda's agricultural sector, improve livelihoods, and
              ensure food security.
            </p>
          </div>
        </div>

        {/* Projects List */}
        <div className="max-w-7xl mx-auto px-4 md:px-8 mt-12" data-id="element-1394">
          <div className="space-y-8" data-id="element-1395">
            {detailedProjects.map((project, index) => {
            const Icon = project.icon;
            return <motion.div key={project.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden flex flex-col lg:flex-row" data-id="element-1396">
                  {/* Left Column - Key Info */}
                  <div className="lg:w-1/3 bg-gray-50 p-6 md:p-8 border-b lg:border-b-0 lg:border-r border-gray-200" data-id="element-1397">
                    <div className="flex items-center gap-4 mb-6" data-id="element-1398">
                      <div className={`w-14 h-14 rounded-xl ${project.color} flex items-center justify-center flex-shrink-0 shadow-sm`} data-id="element-1399">
                        <Icon className="w-7 h-7 text-white" data-id="element-1400" />
                      </div>
                      <div data-id="element-1401">
                        <span className="inline-flex items-center gap-1.5 bg-green-100 text-green-800 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide" data-id="element-1402">
                          <span className="w-2 h-2 bg-green-500 rounded-full" data-id="element-1403" />
                          {project.status}
                        </span>
                      </div>
                    </div>

                    <h2 className="text-2xl font-bold text-uganda-black mb-6 leading-tight" data-id="element-1404">
                      {project.title}
                    </h2>

                    <div className="space-y-4" data-id="element-1405">
                      <div className="flex items-start gap-3" data-id="element-1406">
                        <CalendarIcon className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-1407" />
                        <div data-id="element-1408">
                          <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-0.5" data-id="element-1409">
                            Implementation Period
                          </p>
                          <p className="text-sm font-semibold text-gray-900" data-id="element-1410">
                            {project.period}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3" data-id="element-1411">
                        <DollarSignIcon className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-1412" />
                        <div data-id="element-1413">
                          <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-0.5" data-id="element-1414">
                            Funding Partners
                          </p>
                          <p className="text-sm font-semibold text-gray-900" data-id="element-1415">
                            {project.funder}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3" data-id="element-1416">
                        <TrendingUp className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-1417" />
                        <div data-id="element-1418">
                          <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-0.5" data-id="element-1419">
                            Estimated Budget
                          </p>
                          <p className="text-sm font-semibold text-gray-900" data-id="element-1420">
                            {project.budget}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Details */}
                  <div className="lg:w-2/3 p-6 md:p-8" data-id="element-1421">
                    <h3 className="text-lg font-bold text-uganda-black mb-3" data-id="element-1422">
                      Project Overview
                    </h3>
                    <p className="text-gray-600 leading-relaxed mb-8" data-id="element-1423">
                      {project.description}
                    </p>

                    <h3 className="text-lg font-bold text-uganda-black mb-4" data-id="element-1424">
                      Key Interventions & Highlights
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1425">
                      {project.highlights.map((highlight, i) => <div key={i} className="flex items-start gap-3" data-id="element-1426">
                          <CheckCircle2 className="w-5 h-5 text-uganda-green flex-shrink-0 mt-0.5" data-id="element-1427" />
                          <p className="text-sm text-gray-700 leading-relaxed" data-id="element-1428">
                            {highlight}
                          </p>
                        </div>)}
                    </div>

                    <div className="mt-8 pt-6 border-t border-gray-100" data-id="element-1429">
                      <button className="text-uganda-green font-semibold hover:text-green-800 transition-colors flex items-center gap-2 text-sm" data-id="element-1430">
                        Read Full Project Documentation{' '}
                        <ChevronRight className="w-4 h-4" data-id="element-1431" />
                      </button>
                    </div>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </main>
      <Footer data-id="element-1432" />
    </>;
}